import { useAuth } from "./useAuth";

const ROLE_PERMS: Record<string, Set<string>> = {
  // Own booked treatments on the calendar + Start/End, nothing else.
  therapist: new Set(["view_bookings", "run_treatments"]),
  trainer: new Set(["view_clients", "view_client_notes", "view_training", "schedule_training"]),
  front_desk: new Set([
    "view_clients",
    "view_client_notes",
    "create_client",
    "update_client",
    "write_client_notes",
    "view_treatments",
    "view_bookings",
    "view_all_bookings",
    "run_treatments",
    "create_booking",
    "update_booking",
    "cancel_booking",
    "check_in_booking",
    "mark_no_show",
    "view_promos",
  ]),
  manager: new Set([
    "view_clients",
    "view_client_notes",
    "create_client",
    "update_client",
    "write_client_notes",
    "delete_client",
    "view_audit_logs",
    "view_pos_sync",
    "trigger_pos_sync",
    "view_reports",
    "view_treatments",
    "manage_treatments",
    "view_bookings",
    "view_all_bookings",
    "run_treatments",
    "create_booking",
    "update_booking",
    "cancel_booking",
    "check_in_booking",
    "mark_no_show",
    "view_promos",
    "manage_promos",
  ]),
  // Sales mirrors manager (no user/role management, same as manager).
  sales: new Set([
    "view_clients",
    "view_client_notes",
    "create_client",
    "update_client",
    "write_client_notes",
    "delete_client",
    "view_audit_logs",
    "view_pos_sync",
    "trigger_pos_sync",
    "view_reports",
    "view_treatments",
    "manage_treatments",
    "view_bookings",
    "view_all_bookings",
    "run_treatments",
    "create_booking",
    "update_booking",
    "cancel_booking",
    "check_in_booking",
    "mark_no_show",
    "view_promos",
    "manage_promos",
  ]),
  admin: new Set([
    "view_clients",
    "view_client_notes",
    "create_client",
    "update_client",
    "write_client_notes",
    "delete_client",
    "view_audit_logs",
    "view_pos_sync",
    "trigger_pos_sync",
    "view_reports",
    "view_treatments",
    "manage_treatments",
    "view_bookings",
    "view_all_bookings",
    "run_treatments",
    "create_booking",
    "update_booking",
    "cancel_booking",
    "check_in_booking",
    "mark_no_show",
    "view_promos",
    "manage_promos",
  ]),
};

/** Pure permission check — usable outside React (route beforeLoad guards). */
export function hasPermission(
  user: { role: string; permissions?: readonly string[] | null } | null | undefined,
  perm: string,
): boolean {
  if (!user) return false;
  // The backend resolves effective permissions (editable role map + per-user
  // overrides) and returns them on /me. Prefer that authoritative list; the
  // hardcoded ROLE_PERMS above is only a fallback for older payloads.
  if (Array.isArray(user.permissions)) return user.permissions.includes(perm);
  return ROLE_PERMS[user.role]?.has(perm) ?? false;
}

export function useCan(perm: string): boolean {
  const { user } = useAuth();
  return hasPermission(user, perm);
}
